<!-- views/admin/AdminDashboard.vue -->
<template>
  <main class="dash">

    <!-- ── Header ──────────────────────────────────────── -->
    <div class="dash__header">
      <div>
        <h1 class="dash__title">Tableau de bord</h1>
        <p class="dash__sub">Vue d'ensemble — mise à jour automatique toutes les 30 s</p>
      </div>
      <button class="dash__refresh" :class="{ 'dash__refresh--spinning': loading }" @click="loadDashboardData" title="Rafraîchir">
        <RefreshCw />
      </button>
    </div>

    <!-- ── Global loader ───────────────────────────────── -->
    <div v-if="loading && !hasAnyData" class="dash__loader">
      <span class="dash__ring"></span>
    </div>

    <template v-else>

      <!-- ══════════════════════════════════════════════
           KPI CARDS
      ══════════════════════════════════════════════ -->
      <div class="dash__kpis">

        <!-- Dons collectés -->
        <div class="kpi kpi--forest">
          <div class="kpi__icon"><Heart /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatAmount(donationStats.total_collected) }}</span>
            <span class="kpi__label">Dons collectés</span>
          </div>
          <div class="kpi__sub">
            <span>Ce mois&nbsp;: <strong>{{ formatAmount(donationStats.this_month) }}</strong></span>
            <span class="kpi__count">{{ donationStats.total_count }} donateurs</span>
          </div>
        </div>

        <!-- Jeunes formés -->
        <div class="kpi kpi--terra">
          <div class="kpi__icon"><GraduationCap /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatNumber(formationStats.total_enrolled) }}</span>
            <span class="kpi__label">Jeunes formés</span>
          </div>
          <div class="kpi__sub">
            <span>Sessions actives&nbsp;: <strong>{{ formationStats.active }}</strong></span>
            <span class="kpi__count">{{ formationStats.total }} formations</span>
          </div>
        </div>

        <!-- Projets -->
        <div class="kpi kpi--amber">
          <div class="kpi__icon"><Folder /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatNumber(projectStats.actifs) }}</span>
            <span class="kpi__label">Projets actifs</span>
          </div>
          <div class="kpi__sub">
            <span>Planifiés&nbsp;: <strong>{{ projectStats.planifiés }}</strong></span>
            <span class="kpi__count">{{ projectStats.total }} au total</span>
          </div>
        </div>

        <!-- Bénéficiaires projets -->
        <div class="kpi kpi--sage">
          <div class="kpi__icon"><Users /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatNumber(projectStats.beneficiaires_total) }}</span>
            <span class="kpi__label">Bénéficiaires</span>
          </div>
          <div class="kpi__sub">
            <span>Budget total&nbsp;: <strong>{{ formatAmount(projectStats.budget_total) }}</strong></span>
          </div>
        </div>

        <!-- Contacts -->
        <div class="kpi kpi--cream">
          <div class="kpi__icon"><Mail /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatNumber(contactStats.non_lus) }}</span>
            <span class="kpi__label">Messages non lus</span>
          </div>
          <div class="kpi__sub">
            <span>Urgents&nbsp;: <strong>{{ contactStats.urgents }}</strong></span>
            <span class="kpi__count">{{ contactStats.total }} au total</span>
          </div>
        </div>

        <!-- Dons en attente -->
        <div class="kpi kpi--warn">
          <div class="kpi__icon"><Clock /></div>
          <div class="kpi__body">
            <span class="kpi__value">{{ formatNumber(donationStats.pending_count) }}</span>
            <span class="kpi__label">Dons en attente</span>
          </div>
          <div class="kpi__sub">
            <span>Moy. par don&nbsp;: <strong>{{ formatAmount(donationStats.average_donation) }}</strong></span>
          </div>
        </div>

      </div>

      <!-- ══════════════════════════════════════════════
           CHARTS ROW
      ══════════════════════════════════════════════ -->
      <div class="dash__charts">

        <!-- Évolution mensuelle (line) -->
        <div class="chart-card">
          <div class="chart-card__head">
            <div class="chart-card__title-row">
              <TrendingUp class="chart-card__icon" />
              <h3 class="chart-card__title">Évolution mensuelle</h3>
            </div>
            <div class="chart-card__legend">
              <span><span class="legend-dot" style="background:#1B3A2D"></span>Dons (€)</span>
              <span><span class="legend-dot" style="background:#E8933A"></span>Inscrits</span>
            </div>
          </div>
          <div class="chart-card__body">
            <div v-if="!hasMonthlyData" class="chart-card__empty">
              <BarChart2 />
              <p>Aucune donnée mensuelle disponible</p>
            </div>
            <canvas v-else ref="areaChart"></canvas>
          </div>
        </div>

        <!-- Dons mensuels (bar) -->
        <div class="chart-card">
          <div class="chart-card__head">
            <div class="chart-card__title-row">
              <Heart class="chart-card__icon" />
              <h3 class="chart-card__title">Dons mensuels (€)</h3>
            </div>
          </div>
          <div class="chart-card__body">
            <div v-if="!hasMonthlyData" class="chart-card__empty">
              <BarChart2 />
              <p>Aucune donnée disponible</p>
            </div>
            <canvas v-else ref="barChart"></canvas>
          </div>
        </div>

      </div>

      <!-- ══════════════════════════════════════════════
           BOTTOM ROW
      ══════════════════════════════════════════════ -->
      <div class="dash__bottom">

        <!-- Répartition projets (doughnut) -->
        <div class="chart-card chart-card--sm">
          <div class="chart-card__head">
            <div class="chart-card__title-row">
              <PieChart class="chart-card__icon" />
              <h3 class="chart-card__title">Répartition des projets</h3>
            </div>
          </div>
          <div class="chart-card__body">
            <div v-if="!hasProjectDistribution" class="chart-card__empty">
              <PieChart />
              <p>Aucune donnée disponible</p>
            </div>
            <template v-else>
              <canvas ref="pieChart"></canvas>
              <div class="pie-legend">
                <div
                  v-for="(item, i) in projectsDistribution"
                  :key="item.name"
                  class="pie-legend__item"
                >
                  <span class="pie-legend__dot" :style="{ background: COLORS[i % COLORS.length] }"></span>
                  <span>{{ item.name }}</span>
                  <strong>{{ item.value }}%</strong>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Activité récente -->
        <div class="chart-card chart-card--lg">
          <div class="chart-card__head">
            <div class="chart-card__title-row">
              <Activity class="chart-card__icon" />
              <h3 class="chart-card__title">Activité récente</h3>
            </div>
          </div>
          <div class="chart-card__body chart-card__body--scroll">
            <div v-if="!recentActivities.length" class="chart-card__empty">
              <Activity />
              <p>Aucune activité récente</p>
            </div>
            <div v-else class="activity-list">
              <button
                v-for="(act, i) in recentActivities"
                :key="i"
                class="activity-row"
                @click="openActivityModal(act)"
              >
                <span class="activity-row__dot" :class="`activity-row__dot--${act.type}`"></span>
                <div class="activity-row__content">
                  <p class="activity-row__msg">{{ act.message }}</p>
                  <p class="activity-row__time">{{ act.time }}</p>
                </div>
                <ChevronRight class="activity-row__arrow" />
              </button>
            </div>
          </div>
        </div>

      </div>

      <!-- ══════════════════════════════════════════════
           FORMATIONS & DONS QUICK STATS
      ══════════════════════════════════════════════ -->
      <div class="dash__quick">

        <!-- Formations breakdown -->
        <div class="quick-card">
          <h3 class="quick-card__title"><GraduationCap />Formations</h3>
          <div class="quick-card__rows">
            <div class="quick-row"><span>Actives</span><strong class="quick-row__val--green">{{ formationStats.active }}</strong></div>
            <div class="quick-row"><span>À venir</span><strong>{{ formationStats.upcoming }}</strong></div>
            <div class="quick-row"><span>Complètes</span><strong class="quick-row__val--warn">{{ formationStats.complet }}</strong></div>
            <div class="quick-row quick-row--total"><span>Total</span><strong>{{ formationStats.total }}</strong></div>
          </div>
        </div>

        <!-- Contacts breakdown -->
        <div class="quick-card">
          <h3 class="quick-card__title"><Mail />Messages</h3>
          <div class="quick-card__rows">
            <div class="quick-row"><span>Nouveaux</span><strong class="quick-row__val--warn">{{ contactStats.nouveaux }}</strong></div>
            <div class="quick-row"><span>En cours</span><strong>{{ contactStats.en_cours }}</strong></div>
            <div class="quick-row"><span>Traités</span><strong class="quick-row__val--green">{{ contactStats.traites }}</strong></div>
            <div class="quick-row quick-row--total"><span>Total</span><strong>{{ contactStats.total }}</strong></div>
          </div>
        </div>

        <!-- Projets breakdown -->
        <div class="quick-card">
          <h3 class="quick-card__title"><Folder />Projets</h3>
          <div class="quick-card__rows">
            <div class="quick-row"><span>Actifs</span><strong class="quick-row__val--green">{{ projectStats.actifs }}</strong></div>
            <div class="quick-row"><span>Planifiés</span><strong>{{ projectStats.planifiés }}</strong></div>
            <div class="quick-row"><span>Terminés</span><strong class="quick-row__val--muted">{{ projectStats.terminés }}</strong></div>
            <div class="quick-row quick-row--total"><span>Total</span><strong>{{ projectStats.total }}</strong></div>
          </div>
        </div>

        <!-- Dons par projet -->
        <div class="quick-card">
          <h3 class="quick-card__title"><Heart />Dons par projet</h3>
          <div v-if="!donationStats.by_project?.length" class="quick-card__empty">Aucune donnée</div>
          <div v-else class="quick-card__rows">
            <div
              v-for="proj in donationStats.by_project.slice(0, 5)"
              :key="proj.project_id"
              class="quick-row"
            >
              <span class="quick-row__proj">{{ proj.project_name || 'Général' }}</span>
              <strong>{{ formatAmount(proj.total) }}</strong>
            </div>
          </div>
        </div>

      </div>

    </template>

    <!-- ══════════════════════════════════════════════
         ACTIVITY MODAL
    ══════════════════════════════════════════════ -->
    <Transition name="modal">
      <div v-if="showModal && selectedActivity" class="dash-modal" @click.self="showModal = false">
        <div class="dash-modal__box">
          <div class="dash-modal__head">
            <h3>Détail de l'activité</h3>
            <button @click="showModal = false" class="dash-modal__close"><X /></button>
          </div>
          <div class="dash-modal__body">
            <div class="dash-modal__type-row">
              <span class="activity-row__dot" :class="`activity-row__dot--${selectedActivity.type}`"></span>
              <span class="dash-modal__type-label">{{ typeLabel(selectedActivity.type) }}</span>
            </div>
            <p class="dash-modal__msg">{{ selectedActivity.message }}</p>
            <p class="dash-modal__time">{{ selectedActivity.time }}</p>
          </div>
          <div class="dash-modal__foot">
            <button class="dash-btn" @click="showModal = false">Fermer</button>
          </div>
        </div>
      </div>
    </Transition>

  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Chart, registerables } from 'chart.js'
import {
  TrendingUp, Heart, ChevronRight, X, Users, Folder,
  GraduationCap, Mail, RefreshCw, Clock, Activity,
  PieChart, BarChart2
} from 'lucide-vue-next'

Chart.register(...registerables)

// ── Helpers ──────────────────────────────────────────────
const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']

/**
 * Parse "YYYY-MM" → month index 0-11
 */
function monthIndex(str) {
  if (!str) return -1
  const parts = str.split('-')
  return parseInt(parts[1], 10) - 1
}

export default {
  name: 'AdminDashboard',

  components: {
    TrendingUp, Heart, ChevronRight, X, Users, Folder,
    GraduationCap, Mail, RefreshCw, Clock, Activity,
    PieChart, BarChart2
  },

  data() {
    return {
      COLORS: ['#1B3A2D','#C85A2A','#E8933A','#7A9E87','#EDE4CC','#244D3C'],
      showModal: false,
      selectedActivity: null,
      // chart instances
      _areaChart: null,
      _barChart: null,
      _pieChart: null,
      // auto-refresh handle
      _interval: null
    }
  },

  computed: {
    ...mapState({
      loading:           s => s.loading,
      donationStats:     s => s.donationStats,
      formationStats:    s => s.formationStats,
      projectStats:      s => s.projectStats,
      contactStats:      s => s.contactStats,
      recentActivities:  s => s.recentActivities || []
    }),

    // ── Guard: at least one stat object has real data ─────
    hasAnyData() {
      return (
        this.donationStats?.total_count > 0 ||
        this.formationStats?.total > 0 ||
        this.projectStats?.total > 0 ||
        this.recentActivities.length > 0
      )
    },

    // ── Monthly data from donationStats.by_month ──────────
    monthlyDonations() {
      if (!this.donationStats?.by_month?.length) return []
      // Build an array of 12 slots (index = month 0-11)
      const slots = Array(12).fill(0)
      this.donationStats.by_month.forEach(m => {
        const idx = monthIndex(m.month)
        if (idx >= 0) slots[idx] = Number(m.total) || 0
      })
      return slots
    },

    // ── Monthly enrolled from formationStats.by_month ─────
    monthlyEnrolled() {
      if (!this.formationStats?.by_month?.length) return []
      const slots = Array(12).fill(0)
      this.formationStats.by_month.forEach(m => {
        const idx = monthIndex(m.month)
        if (idx >= 0) slots[idx] = Number(m.count) || 0
      })
      return slots
    },

    hasMonthlyData() {
      return (
        this.monthlyDonations.some(v => v > 0) ||
        this.monthlyEnrolled.some(v => v > 0)
      )
    },

    // ── Projects distribution from projectStats.by_category
    projectsDistribution() {
      const cats = this.projectStats?.by_category
      if (!cats?.length) return []
      const total = cats.reduce((s, c) => s + (c.count || 0), 0)
      if (total === 0) return []
      return cats.map(c => ({
        name: c.category || 'Autre',
        value: Math.round((c.count / total) * 100)
      }))
    },

    hasProjectDistribution() {
      return this.projectsDistribution.length > 0
    }
  },

  watch: {
    // Re-draw charts whenever store data changes
    hasMonthlyData(val) {
      if (val) this.$nextTick(() => this.initCharts())
    },
    hasProjectDistribution(val) {
      if (val) this.$nextTick(() => this.initPieChart())
    },
    // Live update without full re-init
    'donationStats.by_month'() { this.updateCharts() },
    'formationStats.by_month'() { this.updateCharts() },
    'projectStats.by_category'() { this.updatePieChart() }
  },

  created() {
    this.loadDashboardData()
  },

  mounted() {
    this._interval = setInterval(() => this.loadDashboardData(), 30_000)
  },

  beforeUnmount() {
    clearInterval(this._interval)
    this._areaChart?.destroy()
    this._barChart?.destroy()
    this._pieChart?.destroy()
  },

  methods: {
    ...mapActions([
      'fetchDonationStats',
      'fetchFormationStats',
      'fetchProjectStats',
      'fetchContactStats',
      'fetchArticleStats',
      'fetchRecentActivities'
    ]),

    async loadDashboardData() {
      try {
        await Promise.all([
          this.fetchDonationStats(),
          this.fetchFormationStats(),
          this.fetchProjectStats(),
          this.fetchContactStats(),
          this.fetchRecentActivities()
        ])
        // Charts need a tick after store updates
        this.$nextTick(() => this.initCharts())
      } catch (e) {
        console.error('Dashboard load error:', e)
      }
    },

    // ── Chart: Area (line) ──────────────────────────────
    initAreaChart() {
      const el = this.$refs.areaChart
      if (!el) return
      this._areaChart?.destroy()

      const ctx = el.getContext('2d')
      const grad = ctx.createLinearGradient(0, 0, 0, 300)
      grad.addColorStop(0, 'rgba(27,58,45,.25)')
      grad.addColorStop(1, 'rgba(27,58,45,0)')

      this._areaChart = new Chart(el, {
        type: 'line',
        data: {
          labels: MONTHS_FR,
          datasets: [
            {
              label: 'Dons (€)',
              data: this.monthlyDonations,
              borderColor: '#1B3A2D',
              backgroundColor: grad,
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 6
            },
            {
              label: 'Inscrits',
              data: this.monthlyEnrolled,
              borderColor: '#E8933A',
              backgroundColor: 'rgba(232,147,58,.1)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 6
            }
          ]
        },
        options: this.baseLineOptions({
          callbacks: {
            label: ctx => {
              const v = ctx.parsed.y
              return ctx.dataset.label === 'Dons (€)'
                ? `Dons : ${v.toLocaleString('fr-FR')} €`
                : `Inscrits : ${v}`
            }
          }
        })
      })
    },

    // ── Chart: Bar ───────────────────────────────────────
    initBarChart() {
      const el = this.$refs.barChart
      if (!el) return
      this._barChart?.destroy()

      this._barChart = new Chart(el, {
        type: 'bar',
        data: {
          labels: MONTHS_FR,
          datasets: [{
            label: 'Dons (€)',
            data: this.monthlyDonations,
            backgroundColor: '#C85A2A',
            borderRadius: 4,
            borderSkipped: false
          }]
        },
        options: this.baseLineOptions({
          callbacks: {
            label: ctx => `Dons : ${ctx.parsed.y.toLocaleString('fr-FR')} €`
          }
        })
      })
    },

    // ── Chart: Pie ───────────────────────────────────────
    initPieChart() {
      const el = this.$refs.pieChart
      if (!el) return
      this._pieChart?.destroy()

      this._pieChart = new Chart(el, {
        type: 'doughnut',
        data: {
          labels: this.projectsDistribution.map(d => d.name),
          datasets: [{
            data: this.projectsDistribution.map(d => d.value),
            backgroundColor: this.COLORS,
            borderWidth: 0,
            spacing: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '65%',
          plugins: {
            legend: { display: false },
            tooltip: {
              ...this.tooltipDefaults(),
              callbacks: {
                label: ctx => `${ctx.label} : ${ctx.parsed}%`
              }
            }
          }
        }
      })
    },

    initCharts() {
      if (this.hasMonthlyData) {
        this.initAreaChart()
        this.initBarChart()
      }
      if (this.hasProjectDistribution) {
        this.initPieChart()
      }
    },

    updateCharts() {
      if (!this.hasMonthlyData) return
      if (this._areaChart) {
        this._areaChart.data.datasets[0].data = this.monthlyDonations
        this._areaChart.data.datasets[1].data = this.monthlyEnrolled
        this._areaChart.update()
      }
      if (this._barChart) {
        this._barChart.data.datasets[0].data = this.monthlyDonations
        this._barChart.update()
      }
    },

    updatePieChart() {
      if (!this._pieChart || !this.hasProjectDistribution) return
      this._pieChart.data.labels = this.projectsDistribution.map(d => d.name)
      this._pieChart.data.datasets[0].data = this.projectsDistribution.map(d => d.value)
      this._pieChart.update()
    },

    // ── Shared chart option builders ─────────────────────
    tooltipDefaults() {
      return {
        backgroundColor: '#fff',
        titleColor: '#1A1A18',
        bodyColor: '#6b7280',
        borderColor: '#EDE4CC',
        borderWidth: 1,
        cornerRadius: 6,
        padding: 10,
        boxPadding: 4
      }
    },

    baseLineOptions({ callbacks = {} } = {}) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { ...this.tooltipDefaults(), callbacks }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#9ca3af', font: { size: 11 } }
          },
          y: {
            grid: { color: 'rgba(0,0,0,.04)' },
            ticks: { color: '#9ca3af', font: { size: 11 } },
            beginAtZero: true
          }
        }
      }
    },

    // ── Modal ────────────────────────────────────────────
    openActivityModal(act) {
      this.selectedActivity = act
      this.showModal = true
    },

    typeLabel(type) {
      return { formation: 'Formation', don: 'Don', projet: 'Projet', contact: 'Contact', article: 'Article' }[type] || type
    },

    // ── Formatters ───────────────────────────────────────
    formatNumber(n) {
      if (!n) return '0'
      return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n)
    },

    formatAmount(a) {
      if (!a) return '0 €'
      return a >= 1000
        ? (a / 1000).toFixed(1) + 'k €'
        : Number(a).toLocaleString('fr-FR') + ' €'
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:wght@300;400;500&display=swap');

/* ── Tokens ─────────────────────────────────────────────── */
.dash {
  --forest:   #1B3A2D;
  --forest-l: #244D3C;
  --terra:    #C85A2A;
  --amber:    #E8933A;
  --sage:     #7A9E87;
  --cream:    #F5EFE0;
  --cream-d:  #EDE4CC;
  --ink:      #1A1A18;
  --ink-60:   rgba(26,26,24,.6);
  --ink-12:   rgba(26,26,24,.12);
  --white:    #FFFFFF;
  --shadow:   0 2px 12px rgba(26,26,18,.07);
  --shadow-md:0 6px 24px rgba(26,26,18,.11);
  --fd: 'Cormorant Garamond', Georgia, serif;
  --fb: 'DM Sans', sans-serif;

  font-family: var(--fb);
  background: var(--cream);
  color: var(--ink);
  padding: 2rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* ── Header ─────────────────────────────────────────────── */
.dash__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.dash__title {
  font-family: var(--fd);
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.1;
  margin: 0;
}
.dash__sub {
  font-size: .8rem;
  color: var(--ink-60);
  margin: .25rem 0 0;
}
.dash__refresh {
  width: 38px; height: 38px;
  border-radius: 6px; border: 1.5px solid var(--ink-12);
  background: var(--white); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-60); transition: all .2s ease; flex-shrink: 0;
}
.dash__refresh:hover { border-color: var(--terra); color: var(--terra); }
.dash__refresh :deep(svg) { width: 16px; height: 16px; }
.dash__refresh--spinning :deep(svg) { animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Global loader ───────────────────────────────────────── */
.dash__loader {
  display: flex; align-items: center; justify-content: center;
  padding: 5rem;
}
.dash__ring {
  display: block;
  width: 42px; height: 42px; border-radius: 50%;
  border: 3px solid var(--ink-12); border-top-color: var(--terra);
  animation: spin .9s linear infinite;
}

/* ── KPI grid ────────────────────────────────────────────── */
.dash__kpis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}
.kpi {
  border-radius: 8px; padding: 1.5rem;
  box-shadow: var(--shadow);
  display: flex; flex-direction: column; gap: .65rem;
  transition: transform .25s ease, box-shadow .25s ease;
}
.kpi:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }

.kpi--forest { background: var(--forest); }
.kpi--terra  { background: var(--terra); }
.kpi--amber  { background: var(--amber); }
.kpi--sage   { background: var(--sage); }
.kpi--cream  { background: var(--white); border: 1.5px solid var(--ink-12); }
.kpi--warn   { background: var(--cream-d); border: 1.5px solid rgba(200,90,42,.2); }

.kpi__icon {
  width: 36px; height: 36px; border-radius: 6px;
  background: rgba(255,255,255,.18); display: flex; align-items: center; justify-content: center;
}
.kpi--cream .kpi__icon,
.kpi--warn  .kpi__icon { background: rgba(27,58,45,.08); }
.kpi__icon :deep(svg) { width: 18px; height: 18px; color: var(--white); }
.kpi--cream .kpi__icon :deep(svg),
.kpi--warn  .kpi__icon :deep(svg) { color: var(--terra); }

.kpi__body { display: flex; flex-direction: column; gap: .15rem; }
.kpi__value {
  font-family: var(--fd);
  font-size: 1.9rem; font-weight: 700; color: var(--white); line-height: 1;
}
.kpi--cream .kpi__value,
.kpi--warn  .kpi__value { color: var(--ink); }
.kpi__label {
  font-size: .75rem; color: rgba(255,255,255,.7);
  letter-spacing: .05em; text-transform: uppercase;
}
.kpi--cream .kpi__label,
.kpi--warn  .kpi__label { color: var(--ink-60); }

.kpi__sub {
  display: flex; flex-direction: column; gap: .15rem;
  font-size: .78rem; color: rgba(255,255,255,.6);
  border-top: 1px solid rgba(255,255,255,.12); padding-top: .5rem; margin-top: auto;
}
.kpi__sub strong { color: rgba(255,255,255,.9); }
.kpi--cream .kpi__sub,
.kpi--warn  .kpi__sub { color: var(--ink-60); border-top-color: var(--ink-12); }
.kpi--cream .kpi__sub strong,
.kpi--warn  .kpi__sub strong { color: var(--ink); }
.kpi__count { opacity: .65; }

/* ── Chart cards ─────────────────────────────────────────── */
.dash__charts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}
.dash__bottom {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;
}
@media (max-width: 900px) {
  .dash__bottom { grid-template-columns: 1fr; }
}

.chart-card {
  background: var(--white); border-radius: 8px;
  box-shadow: var(--shadow); overflow: hidden;
}
.chart-card--sm { }
.chart-card--lg { }

.chart-card__head {
  padding: 1.25rem 1.5rem .5rem;
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .5rem;
}
.chart-card__title-row {
  display: flex; align-items: center; gap: .5rem;
}
.chart-card__icon { width: 18px; height: 18px; color: var(--terra); flex-shrink: 0; }
.chart-card__title {
  font-family: var(--fd); font-size: 1.1rem; font-weight: 600; color: var(--ink);
}
.chart-card__legend {
  display: flex; gap: 1rem;
}
.chart-card__legend span {
  display: flex; align-items: center; gap: .35rem;
  font-size: .75rem; color: var(--ink-60);
}
.legend-dot {
  display: inline-block; width: 10px; height: 10px; border-radius: 50%;
}
.chart-card__body {
  padding: 1rem 1.25rem 1.5rem;
  height: 280px;
  position: relative;
}
.chart-card__body canvas {
  width: 100% !important; height: 100% !important;
}
.chart-card__body--scroll {
  height: 300px; overflow-y: auto; padding: .5rem 0;
}
.chart-card__empty {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: .6rem; color: var(--ink-60);
}
.chart-card__empty :deep(svg) { width: 32px; height: 32px; color: var(--cream-d); }
.chart-card__empty p { font-size: .85rem; }

/* ── Pie legend ──────────────────────────────────────────── */
.pie-legend {
  margin-top: .75rem;
  display: flex; flex-direction: column; gap: .35rem;
}
.pie-legend__item {
  display: flex; align-items: center; gap: .5rem;
  font-size: .8rem; color: var(--ink-60);
}
.pie-legend__item strong { margin-left: auto; color: var(--ink); font-weight: 600; }
.pie-legend__dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}

/* ── Activity list ───────────────────────────────────────── */
.activity-list { display: flex; flex-direction: column; }
.activity-row {
  display: flex; align-items: center; gap: .75rem;
  padding: .75rem 1.25rem;
  background: none; border: none; cursor: pointer; text-align: left;
  width: 100%; transition: background .2s ease;
}
.activity-row:hover { background: var(--cream); }
.activity-row__dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.activity-row__dot--don       { background: #10b981; }
.activity-row__dot--formation { background: var(--forest); }
.activity-row__dot--projet    { background: var(--terra); }
.activity-row__dot--contact   { background: var(--amber); }
.activity-row__dot--article   { background: var(--sage); }

.activity-row__content { flex: 1; min-width: 0; }
.activity-row__msg {
  font-size: .85rem; font-weight: 500; color: var(--ink);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.activity-row__time { font-size: .75rem; color: var(--ink-60); margin-top: .1rem; }
.activity-row__arrow { width: 16px; height: 16px; color: var(--ink-60); flex-shrink: 0; }

/* ── Quick cards ─────────────────────────────────────────── */
.dash__quick {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}
.quick-card {
  background: var(--white); border-radius: 8px;
  box-shadow: var(--shadow); padding: 1.25rem;
}
.quick-card__title {
  display: flex; align-items: center; gap: .5rem;
  font-family: var(--fd); font-size: 1rem; font-weight: 600; color: var(--ink);
  margin-bottom: 1rem;
}
.quick-card__title :deep(svg) { width: 16px; height: 16px; color: var(--terra); }
.quick-card__rows { display: flex; flex-direction: column; gap: .5rem; }
.quick-card__empty { font-size: .82rem; color: var(--ink-60); padding: .5rem 0; }

.quick-row {
  display: flex; align-items: center; justify-content: space-between;
  font-size: .85rem; color: var(--ink-60);
  padding: .35rem 0; border-bottom: 1px solid var(--cream-d);
}
.quick-row:last-child { border-bottom: none; }
.quick-row--total { margin-top: .25rem; padding-top: .6rem; border-top: 1.5px solid var(--ink-12); border-bottom: none; }
.quick-row--total strong { color: var(--ink); font-size: .95rem; }
.quick-row strong { color: var(--ink); font-weight: 600; }
.quick-row__val--green { color: #10b981 !important; }
.quick-row__val--warn  { color: var(--terra) !important; }
.quick-row__val--muted { color: var(--ink-60) !important; }
.quick-row__proj {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%;
}

/* ── Modal ───────────────────────────────────────────────── */
.dash-modal {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 200; padding: 1.5rem;
}
.dash-modal__box {
  background: var(--white); border-radius: 10px;
  max-width: 420px; width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
}
.dash-modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--ink-12);
}
.dash-modal__head h3 {
  font-family: var(--fd); font-size: 1.1rem; font-weight: 600; color: var(--ink);
}
.dash-modal__close {
  width: 32px; height: 32px; border-radius: 6px; border: none;
  background: var(--cream); cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: var(--ink-60);
  transition: background .2s ease;
}
.dash-modal__close:hover { background: var(--cream-d); }
.dash-modal__close :deep(svg) { width: 16px; height: 16px; }
.dash-modal__body { padding: 1.5rem; display: flex; flex-direction: column; gap: .85rem; }
.dash-modal__type-row { display: flex; align-items: center; gap: .5rem; }
.dash-modal__type-label { font-size: .82rem; font-weight: 500; color: var(--ink-60); }
.dash-modal__msg { font-size: .95rem; color: var(--ink); line-height: 1.55; }
.dash-modal__time { font-size: .8rem; color: var(--ink-60); }
.dash-modal__foot {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--ink-12);
  display: flex; justify-content: flex-end;
}
.dash-btn {
  background: var(--forest); color: var(--white);
  border: none; padding: .65rem 1.5rem; border-radius: 4px;
  font-family: var(--fb); font-size: .88rem; font-weight: 500;
  cursor: pointer; transition: background .2s ease;
}
.dash-btn:hover { background: var(--forest-l); }

/* ── Modal transition ────────────────────────────────────── */
.modal-enter-active, .modal-leave-active { transition: opacity .25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .dash-modal__box,
.modal-leave-active .dash-modal__box { transition: transform .25s ease; }
.modal-enter-from .dash-modal__box, .modal-leave-to .dash-modal__box { transform: scale(.96) translateY(8px); }

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 640px) {
  .dash { padding: 1rem; gap: 1.25rem; }
  .dash__charts { grid-template-columns: 1fr; }
  .dash__quick  { grid-template-columns: 1fr 1fr; }
}
</style>